import React, { MouseEventHandler, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { ChatSessionKey, UserConferenceKey } from 'flow/types/EntityKeys';
import { updateSipProxiesAsGuest } from 'flow/actions/phone-actions';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import {
    setActiveMeetingAudioReferenceAction,
    setDevicesInfo,
} from 'flow/actions/meeting-actions';
import useMeetingDeviceChangeListener from 'flow/hooks/useMeetingDeviceChangeListener';
import { selectViewerIsInMeeting } from 'flow/selectors/meeting-selectors';
import MeetingLobby from '../lobby/MeetingLobby';
import MeetingUnauthorized from '../MeetingUnauthorized';

interface Props {
    meetingLobbyKey?: ChatSessionKey | UserConferenceKey;
    onClickBack?: MouseEventHandler;
    allowToggleNewMembers?: boolean;
    isPersonalMeetingRoom?: boolean;
}
const MeetingMain = ({
    meetingLobbyKey,
    onClickBack,
    allowToggleNewMembers,
    isPersonalMeetingRoom,
}: Props) => {
    const dispatch = useAppDispatch();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    // Not merely "a meeting was opened": someone held in the waiting room
    // (VID-76) has a meeting code and is still outside, and handing them the
    // meeting would be a lie — it would also take away the camera, mic and
    // disconnect controls they are entitled to keep while they wait. The lobby
    // stays put until there is a verdict.
    const viewerIsInMeeting = useAppSelector(selectViewerIsInMeeting);

    const meetingAudioRef = useRef();

    useEffect(() => {
        const setupDevices = async () => {
            await dispatch(setDevicesInfo());
        };
        setupDevices();
    }, [dispatch]);

    useEffect(() => {
        if (!ownExtensionKey) {
            dispatch(
                setActiveMeetingAudioReferenceAction(meetingAudioRef.current),
            );
            updateSipProxiesAsGuest();
        }
    }, [ownExtensionKey, setActiveMeetingAudioReferenceAction]);

    useMeetingDeviceChangeListener();

    const showMeetingView = !ownExtensionKey && viewerIsInMeeting;

    return (
        // The lobby scrolls inside its own page body rather than out here, so
        // its header rule can still span the full width and nothing shifts
        // sideways when a scrollbar appears. The meeting view keeps the outer
        // scrolling it has always had.
        <div
            className={twMerge(
                'flex size-full flex-col',
                showMeetingView ? 'overflow-y-auto' : 'overflow-hidden',
            )}
        >
            <audio ref={meetingAudioRef} autoPlay={true} />

            {showMeetingView ? (
                <MeetingUnauthorized />
            ) : (
                <MeetingLobby
                    meetingLobbyKey={meetingLobbyKey}
                    onClickBack={onClickBack}
                    allowToggleNewMembers={allowToggleNewMembers}
                    isPersonalMeetingRoom={isPersonalMeetingRoom}
                />
            )}
        </div>
    );
};

export default MeetingMain;
